import { useEffect, useRef, useState, useCallback } from "react";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Seo from "@/components/Seo";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Audiences } from "@/components/Audiences";
import { Courses } from "@/components/Courses";
import { CourseModal } from "@/components/CourseModal";
import { Outcomes } from "@/components/Outcomes";
import { Quiz } from "@/components/Quiz";
import { Counselling } from "@/components/Counselling";
import { Founder } from "@/components/Founder";
import { Testimonials } from "@/components/Testimonials";
import { BrandOwners } from "@/components/BrandOwners";
import { CtaStrip } from "@/components/CtaStrip";
import { Footer } from "@/components/Footer";
import { CursorGlow } from "@/components/CursorGlow";
import { getCourses, getConfig, getCourse } from "@/lib/api";
import { WHATSAPP_LINK } from "@/lib/data";
import { MessageCircle } from "lucide-react";

function App() {
  const [courses, setCourses] = useState([]);
  const [config, setConfig] = useState({ payment_mode: "mock", razorpay_key_id: null });
  const [courseModal, setCourseModal] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [counsellingOpen, setCounsellingOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    getCourses().then(setCourses).catch(() => {});
    getConfig().then(setConfig).catch(() => {});
    const testId = new URLSearchParams(window.location.search).get("enroll");
    if (testId) getCourse(testId).then((c) => setCourseModal({ course: c, mode: "enroll" })).catch(() => {});
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const lock = courseModal || quizOpen || counsellingOpen;
    if (lock) lenisRef.current?.stop();
    else lenisRef.current?.start();
  }, [courseModal, quizOpen, counsellingOpen]);

  const scrollTo = useCallback((target) => {
    lenisRef.current?.scrollTo(target, { offset: -70, duration: 1.4 });
  }, []);

  const openQuiz = useCallback(() => {
    setCourseModal(null);
    setCounsellingOpen(false);
    setQuizOpen(true);
  }, []);
  const openCounselling = useCallback(() => {
    setCourseModal(null);
    setQuizOpen(false);
    setCounsellingOpen(true);
  }, []);
  const openCourse = useCallback((course, mode = "details") => {
    setQuizOpen(false);
    setCourseModal({ course, mode });
  }, []);
  const openEnroll = useCallback((course) => openCourse(course, "enroll"), [openCourse]);

  return (
    <div className="grain min-h-screen bg-void">
      <Seo
        title="Sanvalio Academy — Live Online Courses in Marketing, Design & Freelancing"
        siteName="Sanvalio Academy"
        description="Live Google Meet classes with certificates & diplomas in Meta Ads, digital marketing, web design, branding and freelancing. Mentorship by Deepankshi — teaching since 2015, registered 2024. Free career assessment & counselling."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          name: "Sanvalio Academy",
          description:
            "Live online courses in Meta ads, digital marketing, web design, graphic design, personal branding and freelancing, with certificates and diplomas.",
          telephone: "+91 78509 11096",
          email: "support@sanvalioacademy.com",
          founder: { "@type": "Person", name: "Deepankshi" },
          foundingDate: "2024",
        }}
      />
      <CursorGlow />
      <Nav onQuiz={openQuiz} scrollTo={scrollTo} />

      <main>
        <Hero onQuiz={openQuiz} scrollTo={scrollTo} />
        <Marquee />
        <Audiences courses={courses} onCourse={(c) => openCourse(c)} />
        <Courses courses={courses} onCourse={(c) => openCourse(c)} onEnroll={openEnroll} />
        <CtaStrip testId="cta-strip-1" onQuiz={openQuiz} onCounselling={openCounselling} />
        <Outcomes />
        <Founder />
        <Marquee
          testId="marquee-2"
          items={["Meta Ads", "Funnel Mastery", "Landing Pages", "Personal Branding", "Web Design", "Graphic Design", "Freelancing", "Performance Marketing"]}
        />
        <Testimonials />
        <BrandOwners courses={courses} onCourse={(c) => openCourse(c)} onEnroll={openEnroll} />
        <CtaStrip
          testId="cta-strip-2"
          onQuiz={openQuiz}
          onCounselling={openCounselling}
          title={<>Your seat in the next batch is waiting. <em className="text-gold-gradient not-italic">Claim it.</em></>}
        />
      </main>

      <Footer scrollTo={scrollTo} />

      <a
        data-testid="whatsapp-float"
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-leaf text-void shadow-[0_8px_30px_rgba(232,193,90,0.4)] transition-transform duration-300 hover:scale-110"
      >
        <MessageCircle size={22} />
      </a>

      {courseModal && (
        <CourseModal
          key={courseModal.course.id + courseModal.mode}
          course={courseModal.course}
          mode={courseModal.mode}
          paymentMode={config.payment_mode}
          razorpayKey={config.razorpay_key_id}
          onClose={() => setCourseModal(null)}
          onQuiz={openQuiz}
          onCounselling={openCounselling}
        />
      )}
      {quizOpen && <Quiz onClose={() => setQuizOpen(false)} onEnroll={openEnroll} onCounselling={openCounselling} />}
      {counsellingOpen && <Counselling onClose={() => setCounsellingOpen(false)} />}

      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}

export default App;
